import type { Dayjs } from 'dayjs';

import { TIMEZONE_COOKIE_NAME } from '~/modules/shopify/constants';
import { getCookie } from './cookie';

import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import relativeTime from 'dayjs/plugin/relativeTime';
import duration from 'dayjs/plugin/duration';
import timezone from 'dayjs/plugin/timezone';
import isBrowser from './isBrowser';
import utc from 'dayjs/plugin/utc';
import dayjs from 'dayjs';

dayjs.extend(quarterOfYear);

dayjs.extend(relativeTime);
dayjs.extend(duration);
dayjs.extend(utc);
dayjs.extend(timezone);

const DEFAULT_TIMEZONE = 'UTC';

let tz = DEFAULT_TIMEZONE;

const DEFAULT_DATE_FORMAT = 'YYYY-MM-DD HH:mm:ss';

type DateType = string | number | Date | Dayjs | null;

export function getInitialTimezone(): string {
  if (!isBrowser()) {
    return tz;
  }

  return getCookie(TIMEZONE_COOKIE_NAME) || tz;
}

tz = getInitialTimezone();

export function setTz(value: string) {
  tz = value;
  dayjs.tz.setDefault(value);
}

// Convert date to specific timezone (changes time value)
// Example: tz=+5, input: 10:00 → output: 15:00 (5h later)
const dayjsTz = (date?: DateType) => {
  if (!date) return dayjs().tz(tz);
  return dayjs(date).tz(tz);
};

// Convert date to specific timezone (keeps time value, only changes tz)
// Example: tz=+5, input: 10:00 → output: 10:00 (same time, different tz)
const convertDateToTz = (date?: DateType) => {
  if (!date) return dayjs.tz(dayjs().format(DEFAULT_DATE_FORMAT), tz);
  return dayjs.tz(dayjs(date).format(DEFAULT_DATE_FORMAT), tz);
};

// Convert dayjsTz back to local timezone (loses original tz info)
// Example: input: 15:00 UTC+5 → output: 15:00 local (converts back)
const dayjsTzToLocalTZ = (date?: DateType) => {
  if (!date) return dayjs(dayjsTz().format(DEFAULT_DATE_FORMAT));
  return dayjs(dayjsTz(date).format(DEFAULT_DATE_FORMAT));
};

const dayjsTzToDate = (date?: DateType) => {
  return dayjsTzToLocalTZ(date).toDate();
};

export { dayjs, dayjsTz, dayjsTzToDate, convertDateToTz, dayjsTzToLocalTZ };
export type { Dayjs };
